import { useState, useEffect } from "react";
import { listScenarios, getScenario, getDefaults, runSensitivity, fmt, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BarChart3, Loader2, Table2, Activity } from "lucide-react";

const METRIC_OPTIONS = [
  { key: "profit_on_cost", label: "Profit on Cost (%)" },
  { key: "ungeared_irr", label: "Ungeared IRR (%)" },
  { key: "dev_profit", label: "Dev Profit (\u00A3)" },
];

function TornadoChart({ data, metric, base }) {
  if (!data || data.length === 0) return null;
  const baseVal = base[metric];
  const allVals = data.flatMap((d) => d.results.map((r) => r[metric]));
  const gMin = Math.min(...allVals, baseVal);
  const gMax = Math.max(...allVals, baseVal);
  const range = gMax - gMin || 1;
  const pad = range * 0.05;
  const lo = gMin - pad;
  const hi = gMax + pad;
  const totalRange = hi - lo;
  const basePos = ((baseVal - lo) / totalRange) * 100;

  const fmtVal = (v) => {
    if (metric === "dev_profit") return fmt(v);
    return v.toFixed(2) + "%";
  };

  return (
    <div style={{ position: "relative" }} data-testid="tornado-chart">
      {/* Base line */}
      <div
        style={{
          position: "absolute", top: 0, bottom: 0, left: `calc(160px + ${basePos}% * 0.7)`,
          width: 2, background: "hsl(var(--foreground))", zIndex: 5, opacity: 0.3,
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map((variable, vi) => {
          const vals = variable.results.map((r) => r[metric]);
          const vMin = Math.min(...vals);
          const vMax = Math.max(...vals);
          const barLeft = ((vMin - lo) / totalRange) * 100;
          const barRight = ((vMax - lo) / totalRange) * 100;
          const barWidth = barRight - barLeft;
          const isNegativeImpact = vals[0] > vals[vals.length - 1];

          return (
            <div key={vi} style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <div
                style={{
                  width: 160, minWidth: 160, fontSize: 13, fontWeight: 500,
                  textAlign: "right", paddingRight: 16, color: "hsl(var(--foreground))",
                }}
              >
                {variable.label}
              </div>
              <div
                style={{
                  flex: 1, height: 36, position: "relative", background: "hsl(var(--secondary))",
                  borderRadius: 4, overflow: "hidden",
                }}
              >
                {/* Bar */}
                <div
                  style={{
                    position: "absolute", top: 4, bottom: 4,
                    left: `${barLeft * 0.7}%`,
                    width: `${Math.max(barWidth * 0.7, 0.5)}%`,
                    borderRadius: 3,
                    background: isNegativeImpact
                      ? "linear-gradient(90deg, hsl(160 84% 39% / 0.7), hsl(0 84% 60% / 0.7))"
                      : "linear-gradient(90deg, hsl(0 84% 60% / 0.7), hsl(160 84% 39% / 0.7))",
                    transition: "all 0.3s ease",
                  }}
                  data-testid={`tornado-bar-${variable.key}`}
                />
                {/* Base marker */}
                <div
                  style={{
                    position: "absolute", top: 2, bottom: 2,
                    left: `${basePos * 0.7}%`,
                    width: 2, background: "hsl(var(--foreground))", borderRadius: 1,
                  }}
                />
              </div>
              <div style={{ width: 140, minWidth: 140, display: "flex", justifyContent: "space-between", paddingLeft: 12, fontSize: 12 }}>
                <span className="font-mono" style={{ color: "hsl(var(--destructive))" }}>{fmtVal(vMin)}</span>
                <span className="font-mono" style={{ color: "hsl(var(--success))" }}>{fmtVal(vMax)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", marginTop: 12, paddingLeft: 160, fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
        <span>Base case: <strong className="font-mono">{fmtVal(baseVal)}</strong></span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: "hsl(0 84% 60% / 0.7)" }} />Downside
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: "hsl(160 84% 39% / 0.7)" }} />Upside
          </span>
        </span>
      </div>
    </div>
  );
}

function MatrixTable({ matrix, yieldSteps, costSteps, metric, base }) {
  if (!matrix || matrix.length === 0) return null;
  const baseVal = base[metric];

  const fmtCell = (v) => {
    if (metric === "dev_profit") return fmt(v);
    return v.toFixed(2) + "%";
  };

  const cellColor = (v) => {
    const diff = v - baseVal;
    if (Math.abs(diff) < 0.01) return { bg: "hsl(var(--foreground))", color: "white", fontWeight: 700 };
    if (metric === "dev_profit") {
      if (diff > 5000000) return { bg: "hsl(160 84% 39% / 0.15)", color: "hsl(160 84% 29%)" };
      if (diff > 0) return { bg: "hsl(160 84% 39% / 0.08)", color: "hsl(160 84% 35%)" };
      if (diff > -5000000) return { bg: "hsl(0 84% 60% / 0.08)", color: "hsl(0 84% 45%)" };
      return { bg: "hsl(0 84% 60% / 0.15)", color: "hsl(0 84% 35%)" };
    }
    if (diff > 5) return { bg: "hsl(160 84% 39% / 0.15)", color: "hsl(160 84% 29%)" };
    if (diff > 0) return { bg: "hsl(160 84% 39% / 0.08)", color: "hsl(160 84% 35%)" };
    if (diff > -5) return { bg: "hsl(0 84% 60% / 0.08)", color: "hsl(0 84% 45%)" };
    return { bg: "hsl(0 84% 60% / 0.15)", color: "hsl(0 84% 35%)" };
  };

  return (
    <div style={{ overflowX: "auto" }} data-testid="sensitivity-matrix">
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th
              style={{
                padding: "10px 12px", fontSize: 12, fontWeight: 600, textAlign: "left",
                borderBottom: "2px solid hsl(var(--foreground))", color: "hsl(var(--muted-foreground))",
                textTransform: "uppercase", letterSpacing: "0.05em",
              }}
            >
              Yield \ Costs
            </th>
            {costSteps.map((cs) => (
              <th
                key={cs}
                style={{
                  padding: "10px 12px", fontSize: 12, fontWeight: 600, textAlign: "center",
                  borderBottom: "2px solid hsl(var(--foreground))", color: cs === 0 ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                }}
              >
                {cs === 0 ? "Base" : `${cs > 0 ? "+" : ""}${cs}%`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, ri) => (
            <tr key={ri}>
              <td
                style={{
                  padding: "8px 12px", fontSize: 13, fontWeight: 600, fontFamily: "JetBrains Mono",
                  borderBottom: "1px solid hsl(var(--border))",
                  color: yieldSteps[ri] === 0 ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                }}
              >
                {yieldSteps[ri] === 0 ? "Base" : `${yieldSteps[ri] > 0 ? "+" : ""}${yieldSteps[ri]}pp`}
              </td>
              {row.map((cell, ci) => {
                const style = cellColor(cell[metric]);
                return (
                  <td
                    key={ci}
                    style={{
                      padding: "8px 12px", textAlign: "center", fontFamily: "JetBrains Mono",
                      fontSize: 13, borderBottom: "1px solid hsl(var(--border))",
                      background: style.bg, color: style.color, fontWeight: style.fontWeight || 400,
                      transition: "background 0.2s",
                    }}
                    data-testid={`matrix-cell-${ri}-${ci}`}
                  >
                    {fmtCell(cell[metric])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 12 }}>
        Rows: Residential yield change (percentage points) &middot; Columns: Total cost change (%) &middot; <strong>Black cell = base case</strong>
      </div>
    </div>
  );
}

export default function Sensitivity() {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState("defaults");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [metric, setMetric] = useState("profit_on_cost");

  useEffect(() => {
    listScenarios().then(setScenarios).catch(console.error);
  }, []);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      let inputs;
      if (selectedScenario === "defaults") {
        inputs = await getDefaults();
      } else {
        const s = await getScenario(selectedScenario);
        inputs = s.inputs;
      }
      const res = await runSensitivity(inputs);
      setResult(res);
      toast.success("Sensitivity analysis complete");
    } catch (e) {
      toast.error("Analysis failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-enter" style={{ padding: "32px 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Manrope", fontWeight: 800, fontSize: 28 }} data-testid="sensitivity-title">
            Sensitivity Analysis
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, marginTop: 4 }}>
            Stress-test key assumptions and visualise impact on returns
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 24 }}>
        <div style={{ minWidth: 260 }}>
          <Select value={selectedScenario} onValueChange={setSelectedScenario} data-testid="scenario-select">
            <SelectTrigger data-testid="scenario-select-trigger">
              <SelectValue placeholder="Select base scenario" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="defaults">Default Inputs</SelectItem>
              {scenarios.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.project_name || "Untitled"} ({s.location || "N/A"})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={loading}
          style={{ background: "hsl(var(--foreground))", color: "white" }}
          data-testid="run-sensitivity-btn"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />}
          <span style={{ marginLeft: 8 }}>{loading ? "Running..." : "Run Analysis"}</span>
        </Button>
        {result && (
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger style={{ width: 200 }} data-testid="metric-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METRIC_OPTIONS.map((m) => (
                <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {result && (
        <div className="animate-in" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {/* Base case KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {[
              { label: "Base Profit on Cost", value: result.base.profit_on_cost.toFixed(2) + "%" },
              { label: "Base Dev Profit", value: fmt(result.base.dev_profit) },
              { label: "Ungeared IRR", value: result.base.ungeared_irr.toFixed(2) + "%" },
              { label: "Geared IRR", value: result.base.geared_irr.toFixed(2) + "%" },
            ].map((k, i) => (
              <div key={i} className="kpi-card">
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value" style={{ fontSize: 20 }} data-testid={`sensitivity-kpi-${i}`}>{k.value}</div>
              </div>
            ))}
          </div>

          <Tabs defaultValue="tornado" data-testid="sensitivity-tabs">
            <TabsList>
              <TabsTrigger value="tornado" data-testid="tab-tornado">
                <BarChart3 size={14} style={{ marginRight: 6 }} />Tornado Chart
              </TabsTrigger>
              <TabsTrigger value="matrix" data-testid="tab-matrix">
                <Table2 size={14} style={{ marginRight: 6 }} />Yield vs Cost Matrix
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tornado" style={{ marginTop: 20 }}>
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    <BarChart3 size={16} style={{ color: "hsl(var(--accent))" }} />
                    Variable Impact on {METRIC_OPTIONS.find((m) => m.key === metric)?.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TornadoChart data={result.tornado} metric={metric} base={result.base} />
                </CardContent>
              </Card>

              {/* Detail table below tornado */}
              <Card style={{ marginTop: 16 }}>
                <CardHeader>
                  <CardTitle style={{ fontSize: 15 }}>Detailed Stress Results</CardTitle>
                </CardHeader>
                <CardContent style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, textAlign: "left", borderBottom: "2px solid hsl(var(--foreground))", textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))" }}>
                          Variable
                        </th>
                        {(result.tornado[0]?.steps || []).map((s) => (
                          <th key={s} style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, textAlign: "center", borderBottom: "2px solid hsl(var(--foreground))", color: s === 0 ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))" }}>
                            {s === 0 ? "Base" : `${s > 0 ? "+" : ""}${s}${result.tornado[0]?.unit === "pp" ? "pp" : "%"}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.tornado.map((v, vi) => (
                        <tr key={vi}>
                          <td style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500, borderBottom: "1px solid hsl(var(--border))" }}>
                            {v.label}
                          </td>
                          {v.results.map((r, ri) => (
                            <td
                              key={ri}
                              className="font-mono"
                              style={{
                                padding: "8px 12px", textAlign: "center", fontSize: 13,
                                borderBottom: "1px solid hsl(var(--border))",
                                fontWeight: v.steps[ri] === 0 ? 700 : 400,
                                color: r[metric] > result.base[metric]
                                  ? "hsl(160 84% 35%)"
                                  : r[metric] < result.base[metric]
                                  ? "hsl(0 84% 45%)"
                                  : "hsl(var(--foreground))",
                              }}
                            >
                              {metric === "dev_profit" ? fmt(r[metric]) : r[metric].toFixed(2) + "%"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="matrix" style={{ marginTop: 20 }}>
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: 15, display: "flex", alignItems: "center", gap: 8 }}>
                    <Table2 size={16} style={{ color: "hsl(var(--accent))" }} />
                    Yield vs Total Cost Sensitivity &mdash; {METRIC_OPTIONS.find((m) => m.key === metric)?.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <MatrixTable
                    matrix={result.matrix}
                    yieldSteps={result.yield_steps}
                    costSteps={result.cost_steps}
                    metric={metric}
                    base={result.base}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {!result && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BarChart3 size={40} style={{ color: "hsl(var(--muted-foreground))", marginBottom: 16 }} />
            <h3 style={{ fontFamily: "Manrope", fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
              Sensitivity Analysis
            </h3>
            <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14, maxWidth: 420, textAlign: "center", lineHeight: 1.6 }}>
              Select a base scenario and run the analysis to see how profit and IRR change when key assumptions are stressed.
              Variables tested include rents, yields, construction costs, land costs, and finance terms.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
